// Wave 20.44 — About-page storyboards.
//
// Feature-specific animated mocks the About page mounts inside its
// parallax sections. Same design language as DropStoryboard (phone
// frames, keyframe animations, brand-green accents) but every scene
// stands on its own — no cross-scene state.

import React from 'react';
import { Box, Chip, Stack, Typography, keyframes } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import { colors } from '../theme';
import { WindStreakGlyph } from './cheerGlyphs';

// -----------------------------------------------------------------
// Shared animations
// -----------------------------------------------------------------

const pulseGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 ${colors.brand}88; }
  70%  { box-shadow: 0 0 0 14px ${colors.brand}00; }
  100% { box-shadow: 0 0 0 0 ${colors.brand}00; }
`;

const ticker = keyframes`
  0%   { transform: translateY(0); }
  50%  { transform: translateY(-2px); }
  100% { transform: translateY(0); }
`;

const cdaTick = keyframes`
  0%, 100% { content: '0.243'; }
  25%      { content: '0.241'; }
  50%      { content: '0.239'; }
  75%      { content: '0.242'; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  0%   { transform: translateY(24px); opacity: 0; }
  100% { transform: translateY(0);    opacity: 1; }
`;

const gpsBlip = keyframes`
  0%, 100% { transform: scale(1);   opacity: 1;   }
  50%      { transform: scale(1.8); opacity: 0.4; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`;

// -----------------------------------------------------------------
// Frames — phone + laptop chrome
// -----------------------------------------------------------------

export function PhoneFrame({
  children,
  accent,
  size = 'md',
}: {
  children: React.ReactNode;
  accent?: boolean;
  size?: 'sm' | 'md';
}) {
  const w = size === 'sm' ? 240 : 280;
  const h = size === 'sm' ? 480 : 560;
  return (
    <Box
      sx={{
        width: w,
        height: h,
        borderRadius: 6,
        p: 1.25,
        backgroundColor: '#0d0f16',
        border: `1px solid ${accent ? colors.brand : colors.border}`,
        boxShadow: accent
          ? `0 40px 100px ${colors.brand}22, 0 0 0 1px ${colors.brand}44 inset`
          : `0 20px 60px rgba(0,0,0,0.55)`,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 80,
          height: 18,
          borderRadius: 4,
          backgroundColor: '#000',
          zIndex: 2,
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 5,
          overflow: 'hidden',
          backgroundColor: colors.bg,
          position: 'relative',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export function LaptopFrame({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto' }}>
      {/* Screen */}
      <Box
        sx={{
          borderRadius: '14px 14px 4px 4px',
          padding: '10px 10px 12px 10px',
          backgroundColor: '#0d0f16',
          border: `1px solid ${colors.border}`,
          boxShadow: `0 40px 100px rgba(0,0,0,0.55), 0 0 0 1px ${colors.brand}22`,
        }}
      >
        <Box
          sx={{
            aspectRatio: '16 / 10',
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: colors.bg,
            position: 'relative',
          }}
        >
          {children}
        </Box>
      </Box>
      {/* Base + hinge */}
      <Box
        sx={{
          height: 14,
          borderRadius: '0 0 20px 20px',
          background: `linear-gradient(180deg, #1a1e2a 0%, #0b0d13 100%)`,
          mx: 'auto',
          width: '108%',
          transform: 'translateX(-3.7%)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.55)',
        }}
      />
      <Box
        sx={{
          height: 4,
          width: 88,
          borderRadius: '0 0 8px 8px',
          backgroundColor: '#0b0d13',
          mx: 'auto',
          mt: -0.25,
        }}
      />
    </Box>
  );
}

// -----------------------------------------------------------------
// Aero AI — laptop screen showing a live CdA session
// -----------------------------------------------------------------

export function AeroAiScene() {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 0, height: '100%' }}>
      {/* Video area with skeleton overlay + drag heatmap dots */}
      <Box
        sx={{
          position: 'relative',
          background: `radial-gradient(ellipse at 50% 50%, #12141c 0%, #06070a 100%)`,
          overflow: 'hidden',
        }}
      >
        {/* Faux silhouette — SVG rider outline in aero tuck */}
        <Box
          component="svg"
          viewBox="0 0 240 200"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        >
          {/* torso */}
          <path
            d="M60 120 Q100 90 150 100 Q180 105 195 115"
            fill="none"
            stroke={colors.brand}
            strokeWidth="2"
            opacity="0.85"
          />
          {/* arms → bars */}
          <path
            d="M150 100 L200 145"
            fill="none"
            stroke={colors.brand}
            strokeWidth="2"
            opacity="0.85"
          />
          {/* head */}
          <circle cx="50" cy="115" r="12" fill="none" stroke={colors.brand} strokeWidth="2" />
          {/* legs */}
          <path
            d="M120 140 L130 170 M140 145 L155 175"
            fill="none"
            stroke={colors.brand}
            strokeWidth="2"
          />
          {/* joint dots */}
          {[
            [60, 120],
            [100, 108],
            [150, 100],
            [200, 145],
            [130, 170],
            [155, 175],
          ].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="3.5" fill={colors.brand} opacity="0.9" />
          ))}
        </Box>
        {/* Drag heatmap dots — pulsing red/yellow on head + chest */}
        {[
          { cx: '25%', cy: '55%', color: 'rgba(255,120,80,0.55)', size: 44 },
          { cx: '42%', cy: '52%', color: 'rgba(255,190,90,0.42)', size: 36 },
          { cx: '78%', cy: '68%', color: 'rgba(255,90,90,0.5)', size: 30 },
        ].map((d, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: d.cx,
              top: d.cy,
              width: d.size,
              height: d.size,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${d.color} 0%, transparent 70%)`,
              transform: 'translate(-50%, -50%)',
              animation: `${pulseGlow} 2.4s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`,
            }}
          />
        ))}
        {/* LIVE pill top-left */}
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            px: 1,
            py: 0.3,
            borderRadius: 999,
            backgroundColor: 'rgba(10,14,26,0.75)',
            border: `1px solid ${colors.brand}66`,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: colors.brand,
              boxShadow: `0 0 6px ${colors.brand}`,
            }}
          />
          <Typography sx={{ fontSize: '0.55rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.14em' }}>
            LIVE
          </Typography>
        </Box>
        {/* Position chip bottom-left */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            px: 1,
            py: 0.3,
            borderRadius: 999,
            backgroundColor: 'rgba(0,217,255,0.12)',
            border: `1px solid rgba(0,217,255,0.4)`,
          }}
        >
          <Typography sx={{ fontSize: '0.55rem', color: colors.data, fontWeight: 800, letterSpacing: '0.14em' }}>
            AERO TUCK
          </Typography>
        </Box>
      </Box>
      {/* Right sidebar — CdA hero + segments */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${colors.brand}55`,
            backgroundColor: `${colors.brand}0e`,
          }}
        >
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.14em', color: colors.brand }}>
            CdA
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 800,
              fontSize: '1.9rem',
              color: colors.brand,
              lineHeight: 1,
              mt: 0.25,
              textShadow: `0 0 24px ${colors.brand}55`,
              '&::before': {
                content: '"0.241"',
                animation: `${cdaTick} 3s steps(1) infinite`,
              },
            }}
          />
          <Typography sx={{ fontSize: '0.55rem', color: colors.brand, mt: 0.5, fontWeight: 700 }}>
            ↓ 0.006 vs baseline
          </Typography>
        </Box>
        {/* Segment bars */}
        {[
          { label: 'Head', pct: 26 },
          { label: 'Torso', pct: 41 },
          { label: 'Arms', pct: 19 },
          { label: 'Legs', pct: 14 },
        ].map((r, i) => (
          <Box key={r.label} sx={{ animation: `${fadeIn} 400ms ease ${i * 100}ms both` }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography sx={{ fontSize: '0.5rem', color: colors.textMuted, width: 32 }}>{r.label}</Typography>
              <Box sx={{ flex: 1, height: 3, borderRadius: 2, backgroundColor: colors.surfaceElevated, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${r.pct}%`,
                    backgroundColor: colors.brand,
                    animation: `${shimmer} 3s linear infinite`,
                    backgroundImage: `linear-gradient(90deg, ${colors.brand} 0%, ${colors.brandBright} 50%, ${colors.brand} 100%)`,
                    backgroundSize: '200% 100%',
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.55rem',
                  color: colors.brand,
                  width: 22,
                  textAlign: 'right',
                }}
              >
                {r.pct}%
              </Typography>
            </Stack>
          </Box>
        ))}
        {/* Watts saved chip */}
        <Box
          sx={{
            mt: 'auto',
            p: 1,
            borderRadius: 2,
            backgroundColor: `${colors.brand}18`,
            border: `1px solid ${colors.brand}66`,
            textAlign: 'center',
            animation: `${pulseGlow} 2s ease-in-out infinite`,
          }}
        >
          <Typography sx={{ fontSize: '0.55rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.14em' }}>
            WATTS SAVED
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '1.1rem',
              fontWeight: 800,
              color: colors.brand,
              lineHeight: 1,
              mt: 0.25,
            }}
          >
            +11.4 W
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------
// Wake — mini wake using the live embed
// -----------------------------------------------------------------

export function WakeMini() {
  const src =
    '/wake-embed?avgCda=0.26&positions=aero_tuck:0.20:0.008:5,tt:0.22:0.030:3,drops:0.25:0.020:8,hoods:0.30:0.012:6,seated:0.38:0.045:4';
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${colors.border}`,
        boxShadow: `0 40px 100px rgba(0,0,0,0.55), 0 0 0 1px ${colors.brand}22`,
      }}
    >
      <Box
        component="iframe"
        src={src}
        title="Aero Wake preview"
        sx={{
          width: '100%',
          height: '100%',
          border: 0,
          display: 'block',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          pointerEvents: 'none',
        }}
      >
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.18em', color: colors.brand }}>
          AERO WAKE
        </Typography>
        <Typography
          sx={{
            fontSize: '0.6rem',
            color: colors.textMuted,
            fontFamily: '"JetBrains Mono", monospace',
            letterSpacing: '0.08em',
            mt: 0.25,
          }}
        >
          5 positions · 26 sessions
        </Typography>
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------
// Focus — phone showing tiles → banner
// -----------------------------------------------------------------

export function FocusScene() {
  return (
    <Box sx={{ p: 2, pt: 4.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.18em', color: colors.brand }}>
        NEW AERO SESSION
      </Typography>
      <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: colors.text, mt: -0.5 }}>
        What are you working on?
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 0.5 }}>
        {[
          { cls: 'Drops', cda: '0.241', sessions: 8, color: '#6affff', selected: true },
          { cls: 'Aero tuck', cda: '0.219', sessions: 5, color: '#ff7ad9' },
          { cls: 'Hoods', cda: '0.298', sessions: 12, color: '#6aff45' },
          { cls: 'TT', cda: '0.212', sessions: 3, color: '#a18dff' },
        ].map((tile, i) => (
          <Box
            key={tile.cls}
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: colors.surface,
              border: `1px solid ${tile.selected ? colors.brand : colors.border}`,
              boxShadow: tile.selected ? `0 0 0 2px ${colors.brand}44` : 'none',
              animation: `${fadeIn} 400ms ease ${i * 90}ms both`,
            }}
          >
            <Typography sx={{ fontSize: '0.5rem', color: tile.color, fontWeight: 800, letterSpacing: '0.12em' }}>
              REFINE
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: colors.text, fontWeight: 900 }}>{tile.cls}</Typography>
            <Typography sx={{ fontSize: '0.55rem', color: colors.textMuted }}>
              Best{' '}
              <Box component="span" sx={{ color: colors.brand, fontFamily: '"JetBrains Mono", monospace', fontWeight: 700 }}>
                {tile.cda}
              </Box>{' '}
              · {tile.sessions}s
            </Typography>
          </Box>
        ))}
        <Box
          sx={{
            gridColumn: '1 / -1',
            p: 1.25,
            borderRadius: 2,
            border: `1px dashed ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            animation: `${fadeIn} 400ms ease 400ms both`,
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: `${colors.brand}18`,
              border: `1px solid ${colors.brand}66`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.brand,
              fontWeight: 800,
            }}
          >
            +
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: colors.text, fontWeight: 800 }}>
            Record a new position
          </Typography>
        </Box>
      </Box>
      {/* Session opening banner floats at the bottom */}
      <Box
        sx={{
          mt: 'auto',
          p: 1.1,
          borderRadius: 999,
          backgroundColor: 'rgba(10,14,26,0.9)',
          border: `1px solid ${colors.brand}66`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          animation: `${slideUp} 600ms ease 700ms both`,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: colors.brand,
            boxShadow: `0 0 6px ${colors.brand}`,
          }}
        />
        <Typography sx={{ fontSize: '0.65rem', color: colors.text, fontWeight: 700, lineHeight: 1.25 }}>
          <Box component="span" sx={{ color: colors.brand, fontWeight: 800, letterSpacing: '0.08em' }}>
            DROPS
          </Box>{' '}
          — best so far{' '}
          <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', color: colors.brand, fontWeight: 700 }}>
            0.241
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------
// Recording — phone with live tiles + peak notification
// -----------------------------------------------------------------

export function RecordingScene() {
  return (
    <Box sx={{ p: 1.75, pt: 4.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pt: 0.5 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: colors.danger,
            animation: `${gpsBlip} 1.4s ease-in-out infinite`,
          }}
        />
        <Typography sx={{ fontSize: '0.6rem', color: colors.textMuted, fontWeight: 700 }}>
          Recording · 32:14
        </Typography>
      </Stack>
      <Box
        sx={{
          p: 1,
          borderRadius: 2,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Typography sx={{ fontSize: '0.5rem', color: colors.textMuted, fontWeight: 700, letterSpacing: '0.12em' }}>
          SPEED
        </Typography>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 900,
            fontSize: '1.35rem',
            color: colors.text,
            animation: `${ticker} 2s ease-in-out infinite`,
          }}
        >
          34.2 mph
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        {[
          { l: 'Dist', v: '18.4 mi' },
          { l: 'Time', v: '32:14' },
        ].map((t) => (
          <Box
            key={t.l}
            sx={{
              flex: 1,
              p: 0.75,
              borderRadius: 1.5,
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography sx={{ fontSize: '0.45rem', color: colors.textMuted, fontWeight: 700, letterSpacing: '0.12em' }}>
              {t.l.toUpperCase()}
            </Typography>
            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '0.75rem' }}>
              {t.v}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        {[
          { l: 'HR', v: '168 bpm' },
          { l: 'Pwr', v: '312 W' },
        ].map((t) => (
          <Box
            key={t.l}
            sx={{
              flex: 1,
              p: 0.75,
              borderRadius: 1.5,
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography sx={{ fontSize: '0.45rem', color: colors.textMuted, fontWeight: 700, letterSpacing: '0.12em' }}>
              {t.l.toUpperCase()}
            </Typography>
            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '0.75rem' }}>
              {t.v}
            </Typography>
          </Box>
        ))}
      </Box>
      {/* Fake terrain sparkline */}
      <Box
        sx={{
          height: 40,
          borderRadius: 1.5,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 300 40"
          preserveAspectRatio="none"
          sx={{ width: '100%', height: '100%' }}
        >
          <path
            d="M0 30 Q60 15 120 22 T240 18 T300 28"
            fill="none"
            stroke={colors.brand}
            strokeWidth="1.5"
          />
          <circle cx="180" cy="20" r="3" fill={colors.brand}>
            <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
          </circle>
        </Box>
      </Box>
      {/* Peak notification — auto-appears */}
      <Box
        sx={{
          mt: 'auto',
          p: 1.1,
          borderRadius: 2,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.brand}66`,
          animation: `${slideUp} 600ms ease 900ms both`,
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          <BoltIcon sx={{ color: colors.brand, fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.55rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.14em' }}>
            NEW PEAK
          </Typography>
        </Stack>
        <Typography sx={{ fontWeight: 800, color: colors.text, fontSize: '0.8rem', mt: 0.25 }}>
          Best 5-min Power
        </Typography>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 900,
            fontSize: '1.1rem',
            color: colors.brand,
            lineHeight: 1,
            mt: 0.25,
          }}
        >
          320 W
        </Typography>
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------
// Weather + wind — forecast card with sparklines and wind chip
// -----------------------------------------------------------------

export function WeatherScene() {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        boxShadow: `0 20px 60px rgba(0,0,0,0.4)`,
        width: '100%',
        maxWidth: 380,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.18em', color: colors.brand }}>
          WEATHER
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          icon={<Box sx={{ display: 'inline-flex', ml: 0.5 }}><WindStreakGlyph size={12} /></Box>}
          label="Calm"
          size="small"
          sx={{
            backgroundColor: 'rgba(30,30,38,0.92)',
            color: colors.text,
            fontWeight: 800,
            letterSpacing: '0.06em',
          }}
        />
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <ThermostatIcon sx={{ color: colors.brand, fontSize: 34 }} />
        <Box>
          <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 900, fontSize: '1.6rem', color: colors.text }}>
            68°F
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: colors.textMuted }}>Clear · 52% humidity</Typography>
        </Box>
      </Stack>
      {/* 8-hour comfort strip */}
      <Stack direction="row" spacing={0.5}>
        {[6, 7, 8, 9, 8, 6, 4, 3, 3].map((c, i) => {
          const t = c / 10;
          const bg = `hsl(${Math.round(140 * t + 10 * (1 - t))}, 78%, ${50 + 6 * t}%)`;
          return (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: 26,
                borderRadius: 1,
                backgroundColor: bg,
                opacity: i === 0 ? 1 : 0.72,
                animation: `${fadeIn} 400ms ease ${i * 60}ms both`,
              }}
            />
          );
        })}
      </Stack>
      <Typography sx={{ fontSize: '0.6rem', color: colors.textMuted, letterSpacing: '0.14em', fontWeight: 700 }}>
        NEXT 8H · GOOD → BAD
      </Typography>
      {/* Wind sparkline row */}
      <Stack direction="row" spacing={1} alignItems="center">
        <AirIcon sx={{ color: colors.data, fontSize: 18 }} />
        <Box sx={{ flex: 1, height: 22 }}>
          <Box component="svg" viewBox="0 0 100 22" preserveAspectRatio="none" sx={{ width: '100%', height: '100%' }}>
            <path
              d="M0 12 L12 10 L25 14 L38 8 L52 12 L65 6 L78 10 L90 14 L100 11"
              fill="none"
              stroke={colors.data}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </Box>
        </Box>
        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', color: colors.data, fontWeight: 800 }}>
          3.4 mph
        </Typography>
      </Stack>
    </Box>
  );
}

// -----------------------------------------------------------------
// Drops teaser — mini phone with the drop link chat bubble
// -----------------------------------------------------------------

export function DropsTeaserScene() {
  return (
    <Box sx={{ p: 2, pt: 4.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography sx={{ fontSize: '0.55rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.14em' }}>
        DROP LINK COPIED
      </Typography>
      <Box
        sx={{
          p: 1.1,
          borderRadius: 2,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.brand}66`,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.65rem',
            color: colors.text,
            wordBreak: 'break-all',
          }}
        >
          aero.chat/dropped/9k4x2mvyq3
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.6rem', color: colors.textMuted }}>Paste it anywhere:</Typography>
      {/* Chat bubbles */}
      <Box
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '80%',
          p: 0.9,
          borderRadius: 2,
          borderBottomRightRadius: 4,
          backgroundColor: colors.brand,
          color: '#0A100C',
          fontSize: '0.65rem',
          fontFamily: '"JetBrains Mono", monospace',
          animation: `${slideUp} 500ms ease 200ms both`,
        }}
      >
        aero.chat/dropped/9k4x2mvyq3
      </Box>
      <Box
        sx={{
          alignSelf: 'flex-start',
          maxWidth: '75%',
          p: 0.9,
          borderRadius: 2,
          borderBottomLeftRadius: 4,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          fontSize: '0.7rem',
          color: colors.text,
          animation: `${slideUp} 500ms ease 900ms both`,
        }}
      >
        haha ok, I'll try to beat that 👀
      </Box>
      <Box
        sx={{
          mt: 'auto',
          textAlign: 'center',
          fontSize: '0.55rem',
          color: colors.textMuted,
          letterSpacing: '0.08em',
        }}
      >
        Expires in 24 h · Anyone with the link
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------
// Feed — stack of ride + drop + aero cards
// -----------------------------------------------------------------

export function FeedScene() {
  const cards: Array<{
    icon: React.ReactNode;
    title: string;
    meta: string;
    chip: React.ReactNode;
  }> = [
    {
      icon: <DirectionsBikeIcon sx={{ color: colors.brand, fontSize: 22 }} />,
      title: 'Robby · Ride',
      meta: '4.25 mi · 21m · 11.9 mph',
      chip: (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <WindStreakGlyph size={10} />
          <span>Calm</span>
        </Box>
      ),
    },
    {
      icon: <BoltIcon sx={{ color: colors.brand, fontSize: 22 }} />,
      title: 'Sarah · Best 5-min Power',
      meta: '320 W · 5:00',
      chip: 'DROP',
    },
    {
      icon: <LocationOnIcon sx={{ color: colors.brand, fontSize: 22 }} />,
      title: 'Alex · Aero session',
      meta: 'Drops · CdA 0.241',
      chip: 'AERO',
    },
  ];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, width: '100%', maxWidth: 340 }}>
      {cards.map((c, i) => (
        <Box
          key={i}
          sx={{
            p: 1.5,
            borderRadius: 3,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            animation: `${slideUp} 600ms ease ${i * 200}ms both`,
            boxShadow: `0 12px 32px rgba(0,0,0,0.4)`,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              backgroundColor: `${colors.brand}12`,
              border: `1px solid ${colors.brand}44`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {c.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: colors.text, fontWeight: 800, fontSize: '0.85rem' }}>{c.title}</Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace' }}>
              {c.meta}
            </Typography>
          </Box>
          <Chip
            label={c.chip}
            size="small"
            sx={{
              backgroundColor: `${colors.brand}18`,
              color: colors.brand,
              fontWeight: 800,
              fontSize: '0.6rem',
              letterSpacing: '0.08em',
              border: `1px solid ${colors.brand}44`,
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

// -----------------------------------------------------------------
// Wave 20.49ar — Aero Intensity storyboard.
// A card that mirrors the real IntensityCard: eyebrow with a
// small (i), a subtitle, a horizontal min→median→max bar with
// this session's marker, and a percentile chip. Animated in
// with a subtle marker slide so it feels alive.
// -----------------------------------------------------------------
export function IntensityScene() {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 380,
        p: 2.5,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        boxShadow: `0 24px 60px rgba(0,0,0,0.4)`,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
        <Typography
          sx={{
            color: colors.brand,
            fontSize: '0.65rem',
            fontWeight: 800,
            letterSpacing: '0.16em',
          }}
        >
          AERO INTENSITY ⓘ
        </Typography>
        <Chip
          label="78TH PERCENTILE"
          size="small"
          sx={{
            backgroundColor: 'transparent',
            border: `1px solid ${colors.brand}`,
            color: colors.brand,
            fontWeight: 800,
            fontSize: '0.6rem',
            letterSpacing: '0.08em',
            height: 20,
          }}
        />
      </Box>
      <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', mb: 2 }}>
        Aero effort (25.3 mph) vs your last 14 rides.
      </Typography>
      <Box sx={{ position: 'relative', height: 8, borderRadius: 4, backgroundColor: colors.border }}>
        {/* Median tick */}
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            left: '50%',
            width: 2,
            height: 12,
            backgroundColor: colors.textMuted,
            transform: 'translateX(-1px)',
          }}
        />
        {/* Current-session marker with an animated slide-in */}
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            left: '78%',
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: colors.brand,
            border: `2px solid ${colors.brand}`,
            transform: 'translateX(-8px)',
            animation: `${slideUp} 700ms cubic-bezier(.2,.7,.2,1) 200ms both`,
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
        <Typography sx={{ color: colors.textFaint, fontSize: '0.65rem', fontFamily: '"JetBrains Mono", monospace' }}>
          18.2 mph
        </Typography>
        <Typography sx={{ color: colors.textFaint, fontSize: '0.65rem', fontFamily: '"JetBrains Mono", monospace' }}>
          31.4 mph
        </Typography>
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------
// Analytics — animated CdA × duration curve
// -----------------------------------------------------------------

export function AnalyticsScene() {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        boxShadow: `0 20px 60px rgba(0,0,0,0.4)`,
        width: '100%',
        maxWidth: 440,
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.6rem', color: colors.brand, fontWeight: 800, letterSpacing: '0.18em' }}>
          CdA × DURATION
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.7rem', color: colors.textMuted }}>Last 30 days</Typography>
      </Stack>
      <Box
        component="svg"
        viewBox="0 0 320 140"
        sx={{ width: '100%', height: 140, display: 'block' }}
      >
        {/* grid */}
        {[30, 60, 90, 120].map((y) => (
          <line key={y} x1="30" x2="310" y1={y} y2={y} stroke={colors.border} strokeWidth="0.5" />
        ))}
        {/* mean-maximal curve */}
        <path
          d="M30 110 Q60 92 100 78 T190 60 T310 44"
          fill="none"
          stroke={colors.brand}
          strokeWidth="2"
          strokeDasharray="380"
          strokeDashoffset="380"
          style={{ animation: `dashDraw 1600ms ease-out 200ms forwards` }}
        />
        <style>
          {`@keyframes dashDraw { to { stroke-dashoffset: 0; } }`}
        </style>
        {/* scatter dots — recent sessions */}
        {[
          [60, 100, 0.9],
          [95, 85, 0.7],
          [130, 78, 1],
          [175, 68, 0.85],
          [220, 62, 0.75],
          [265, 55, 0.9],
          [300, 48, 1],
        ].map(([x, y, o], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r="3.5"
            fill={colors.brand}
            opacity={o}
            style={{ animation: `${fadeIn} 400ms ease ${400 + i * 100}ms both` }}
          />
        ))}
        {/* axis labels */}
        <text x="30" y="135" fill={colors.textMuted} fontSize="8" fontFamily="JetBrains Mono, monospace">
          5s
        </text>
        <text x="150" y="135" fill={colors.textMuted} fontSize="8" fontFamily="JetBrains Mono, monospace">
          5min
        </text>
        <text x="285" y="135" fill={colors.textMuted} fontSize="8" fontFamily="JetBrains Mono, monospace">
          60min
        </text>
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.55rem', color: colors.textMuted, letterSpacing: '0.12em', fontWeight: 700 }}>
            LIFETIME BEST
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 900,
              fontSize: '1.1rem',
              color: colors.brand,
            }}
          >
            0.212
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.55rem', color: colors.textMuted, letterSpacing: '0.12em', fontWeight: 700 }}>
            SESSIONS
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 900,
              fontSize: '1.1rem',
              color: colors.text,
            }}
          >
            26
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.55rem', color: colors.textMuted, letterSpacing: '0.12em', fontWeight: 700 }}>
            POSITIONS
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 900,
              fontSize: '1.1rem',
              color: colors.text,
            }}
          >
            5
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
